import { v4 as uuid } from 'uuid';
import { ExecArray } from 'xregexp';
import meanBy from 'lodash/meanBy';
import uniq from 'lodash/uniq';
import getConfig from 'next/config';
import { useCallback, useEffect, useRef, useState } from 'react';
import XRegExp from '@replacements/utils/XRegExp';
import { useCanUseTeamView } from 'abilities/team';
import { useFeatureFlags } from '../../components/FeatureFlags';
import { ClipActor } from '../../../server/graphql/__generated.types';
import { useUserSubscription } from '../../components/Account/operation.generated';
import { useProductUsageQuery } from '../../components/ProductUsage/operation.generated';
import { usePromptsContext } from '../prompts/PromptsContext';
import useTTS from './useTTS';
import useClipCacheUtilities from '../../components/StudioClipUserList/hooks/useStudioClips/useClipCacheUtilities';
import { useRequestCancelClipRenderingMutation } from '../../components/Editor/operation.generated';
import { EventName, useEventsContext } from '../events/EventsContext';
import usePreviewTTS, { PreviewClip } from './usePreviewTTS';
import { getCoreEditorRef } from '../editorV2/CoreEditorContext';
import { useAppSelector } from '../../store/hooks';
import { generateState } from '../../store/editorUtils';
import useEditorFormattedText from '../../components/Editor/useEditorFormattedText';
import useSRAnnouncer from '../../lib/useSRAnnouncer';
import storage from '../../util/storage';
import { CLIP_PREVIEWS } from '../previewClip/StudioPreviewClipContext';
import { useIsManagedUser, useIsTrialUser } from '../user/UserContext';

const { TAG_NAME } = getConfig().publicRuntimeConfig;

type SubmitFunction = (options?: {
  canAccessCuesMode: boolean;
}) => Promise<any>;
type CancelFunction = () => Promise<any>;
type PreviewFunction = () => Promise<{ textFormat: string } | void>;
type ClipCreatorState = {
  loading: boolean;
  error: string | null;
  currentClipId?: string;
};
type ResetPreviewClip = () => void;
type ResetPreviewClipAndState = () => void;

const initialState: ClipCreatorState = {
  error: null,
  loading: false,
  currentClipId: undefined,
};

export const TEAM_ERROR_MSG =
  'Your team has reached the clip generation limit for this billing period. Contact sales to increase this limit.';
export const USER_ERROR_MSG =
  'You have reached the clip generation limit for this billing period. Contact sales to increase this limit.';

/**
 * Small frontend assertion that throws error if user would exceed their
 * limit on the attempted render.
 *
 * @throws (will exceed limit error)
 */
export function assertUserWillNotExceedClipGenerationLimit({
  clipGenerationLimit,
  clipsGenerated,
  clipsAttemptingToRender,
  errorMessage,
  onBeforeThrow = () => {},
}: {
  clipGenerationLimit: number;
  clipsGenerated: number;
  clipsAttemptingToRender: number;
  errorMessage?: string;
  onBeforeThrow?: () => void;
}) {
  if (clipGenerationLimit > -1) {
    // Ensure user does not exceed the clip limit
    const clipsRemaining = Math.max(clipGenerationLimit - clipsGenerated, 0);
    if (clipsRemaining < clipsAttemptingToRender) {
      onBeforeThrow();
      throw new Error(errorMessage || `User will exceed clip limit.`);
    }
  }
}

/**
 * This hook provides the ability to generate clips within a specific project. It pulls the editor
 * state directly from the EditorContext as apposed to passing it in as an argument to the submit
 * function. This will likely change when moving towards inline editing.
 */
function useClipCreator(
  projectId: string,
  actor: ClipActor,
  setUpgradePlanError?: (upgradePlanError: boolean) => void
): [
  SubmitFunction,
  ClipCreatorState,
  CancelFunction,
  PreviewFunction,
  PreviewClip,
  ResetPreviewClip,
  ResetPreviewClipAndState
] {
  const canceledRef = useRef(false);
  const [state, setState] = useState<ClipCreatorState>({
    loading: false,
    error: null,
    currentClipId: undefined,
  });
  const [eventTracking] = useEventsContext();
  const canUseTeamView = useCanUseTeamView();
  const [renderTTS] = useTTS(projectId);
  const [{ clip: previewClip }, previewTTS, resetPreviewClip] =
    usePreviewTTS(projectId);
  const { data: usageData } = useProductUsageQuery();
  const [, dispatch] = usePromptsContext();
  const isManagedUser = useIsManagedUser();
  const isTrialAccount = useIsTrialUser();
  const { data: subscriptionData } = useUserSubscription();
  const [cancelRendering] = useRequestCancelClipRenderingMutation();
  const { removeClipFromCache } = useClipCacheUtilities();
  const coreEditor = getCoreEditorRef();
  const editorState = useAppSelector(appState => appState.editor);
  const [getFormattedText] = useEditorFormattedText();
  const { announce } = useSRAnnouncer();
  const { isFeatureEnabled } = useFeatureFlags();

  const resetState = useCallback(() => {
    canceledRef.current = false;
    setState(initialState);
  }, []);

  const setErrorState = useCallback((errorMessage: string) => {
    canceledRef.current = false;
    setState({
      ...initialState,
      error: errorMessage,
    });
  }, []);

  const resetPreviewClipAndState = () => {
    resetPreviewClip();
    resetState();
  };

  const trackBulkClipCreationMixPanelEvent = useCallback(
    (
      clipsTexts: Array<string>,
      charsUsed: number,
      renderMode: 'all' | 'sentence' | 'paragraph'
    ) => {
      if (renderMode !== 'all') {
        eventTracking.track({
          type: EventName.ClipRenderedBatch,
          data: {
            actorId: `${actor.id}`,
            actorVariantId: `${actor.variant.id}`,
            actorVariantType: actor.variant.variantType.label,
            actorName: actor.name,
            totalLength: charsUsed,
            totalClips: clipsTexts.length,
            minLength: Math.min(...clipsTexts.map(chunk => chunk.length)),
            maxLength: Math.max(...clipsTexts.map(chunk => chunk.length)),
            meanLength: Math.round(meanBy(clipsTexts, chunk => chunk.length)),
            renderMode,
          },
        });
      }
    },
    [
      actor.id,
      actor.name,
      actor.variant.id,
      actor.variant.variantType.label,
      eventTracking,
    ]
  );

  const verifyProductAccessLimits = useCallback(
    (
      renderMode: string,
      textInput: Array<string>
    ): {
      error?: string;
      success: boolean;
    } => {
      try {
        if (
          usageData &&
          usageData.user.productAccessLimits &&
          usageData.user.productAccessPeriodUsage
        ) {
          const teamErrorMessage =
            renderMode === 'all'
              ? TEAM_ERROR_MSG
              : `Rendering by ${renderMode} will exceed your team's clips generation limit for this billing period.`;
          const userErrorMessage =
            renderMode === 'all'
              ? `You've hit your ${
                  usageData.user.productAccessLimits.takeCountGenerationLimit ??
                  0
                }-clip limit for ${
                  isTrialAccount ? 'trial accounts' : 'this billing period'
                }`
              : `Rendering by ${renderMode} will exceed your clips generation limit for this billing period.`;

          assertUserWillNotExceedClipGenerationLimit({
            clipGenerationLimit:
              // legacy studio users should always be limited by clip/take count
              usageData.user.productAccessLimits.takeCountGenerationLimit ?? 0,
            clipsGenerated:
              usageData.user.productAccessPeriodUsage.clipsGenerated +
              usageData.user.productAccessPeriodUsage.takesGenerated,
            clipsAttemptingToRender: textInput.length,
            errorMessage: canUseTeamView ? teamErrorMessage : userErrorMessage,
            onBeforeThrow: () => {
              if (isManagedUser) {
                return;
              }
              if (!canUseTeamView) {
                if (setUpgradePlanError) {
                  setUpgradePlanError(true);
                }

                dispatch({
                  type: 'OpenUpgradePlanPrompt',
                  payload: {
                    isTrialAccount,
                    isDownloadLimit: false,
                    limitAmount:
                      usageData.user.productAccessLimits
                        .takeCountGenerationLimit ?? 0,
                    onClick: () => {
                      if (setUpgradePlanError) {
                        setUpgradePlanError(false);
                      }
                      setState((prevState: ClipCreatorState) => ({
                        ...prevState,
                        error: null,
                      }));
                      dispatch({
                        type: 'OpenSubscriptionTiersPrompt',
                        payload: {
                          upgradeContext: 'access_limit_clips',
                          upgradePath: 'Dashboard > Studio > Clips Limit Modal',
                        },
                      });
                    },
                  },
                });
              }
            },
          });
        }

        return {
          success: true,
        };
      } catch (error) {
        if (!(error instanceof Error)) throw error;
        const { message } = error;
        setState((prevState: ClipCreatorState) => ({
          ...prevState,
          loading: false,
          error: message,
        }));

        return {
          error: message,
          success: false,
        };
      }
    },
    [
      usageData,
      isTrialAccount,
      canUseTeamView,
      isManagedUser,
      setUpgradePlanError,
      dispatch,
    ]
  );

  const renderClip = useCallback(
    async (
      plainText: string,
      formattedText: string | undefined,
      renderMode: 'all' | 'sentence' | 'paragraph'
    ) => {
      const clipId = uuid();
      setState((prevState: ClipCreatorState) => ({
        ...prevState,
        currentClipId: clipId,
      }));

      const cues = coreEditor?.helpers.findCues();

      await renderTTS({
        clipId,
        projectId,
        actor,
        textInput: plainText,
        textFormat: formattedText,
        renderMode,
        ttsVersion: actor.ttsVersion ?? undefined,
        cues,
      });
    },
    [actor, coreEditor, projectId, renderTTS]
  );

  const previewFn = useCallback(async () => {
    const renderMode = 'all';
    const { text: textFromEditor } = editorState.script;
    let text = textFromEditor;
    const storedPreviews = storage.getItem(CLIP_PREVIEWS);
    if (storedPreviews) {
      try {
        const arr = JSON.parse(storedPreviews);
        text = arr[0].text;
      } catch (err) {
        // todo: find out if we should delete...
        console.warn(
          'useClipCreator:previewFn: Unable to parse stored preview data'
        );
      }
    }
    const { textFormat } = getFormattedText({
      canAccessCuesMode: true,
      renderMode,
    });

    setState({
      loading: true,
      error: null,
      currentClipId: undefined,
    });
    canceledRef.current = false;

    const result = verifyProductAccessLimits(renderMode, [textFormat]);

    if (!result.success) {
      setState({
        loading: false,
        error: result.error || null,
        currentClipId: undefined,
      });

      return;
    }

    const clipId = uuid();
    setState({
      loading: true,
      error: null,
      currentClipId: clipId,
    });

    const cues = coreEditor?.helpers.findCues();

    await previewTTS({
      clipId,
      projectId,
      actor,
      textInput: text,
      textFormat,
      renderMode,
      ttsVersion: actor.ttsVersion ?? undefined,
      cues,
    })
      .then(() => {
        resetState();
      })
      .catch(err => {
        setErrorState(err);
      });

    return { textFormat };
  }, [
    editorState.script,
    getFormattedText,
    verifyProductAccessLimits,
    coreEditor?.helpers,
    previewTTS,
    projectId,
    actor,
    resetState,
    setErrorState,
  ]);

  const submitFn = useCallback(
    async (
      { canAccessCuesMode }: { canAccessCuesMode: boolean } = {
        canAccessCuesMode: false,
      }
    ) => {
      setState({
        loading: true,
        error: null,
        currentClipId: undefined,
      });
      canceledRef.current = false;

      const clipTextsFinishedRendering: Array<string> = [];

      // Current editor state will have this set by the time user clicks create
      const { renderMode, script } = editorState;
      const { text } = script;

      // Need to get text format for the first time
      const { textFormat } = getFormattedText({
        canAccessCuesMode,
        renderMode,
      });

      // Generate the state to get new final script and any errors
      const generatedState = generateState(editorState, {
        renderMode: editorState.renderMode,
        text: editorState.script.text,
        textFormat,
        json: editorState.script.json,
      });
      const { error } = generatedState;
      const { scriptArrayToRender: input, charactersUsed } =
        generatedState.finalScript;

      const result = verifyProductAccessLimits(renderMode, input);

      if (!result.success) {
        setState({
          loading: false,
          error: result.error || null,
          currentClipId: undefined,
        });

        return;
      }
      // todo: how does clipTextsFinishedRendering have an effect here if array should be empty?
      // Actual text input validation
      try {
        if (error) {
          return setState({
            loading: false,
            error: error.message,
            currentClipId: undefined,
          });
        }

        if (canceledRef.current && clipTextsFinishedRendering.length) {
          trackBulkClipCreationMixPanelEvent(
            clipTextsFinishedRendering,
            charactersUsed,
            renderMode
          );
          return resetState();
        }
      } catch (e) {
        if (!(e instanceof Error)) throw e;
        return setState({
          loading: false,
          error: e.message,
          currentClipId: undefined,
        });
      }

      if (renderMode === 'all') {
        if (canceledRef.current) {
          return resetState();
        }
        // both text arrays would be an array of single item for single take rendering
        announce('Rendering new clip.');
        await renderClip(text, textFormat, renderMode);
        clipTextsFinishedRendering.push(text);
        announce('New clip rendered.');
      } else {
        announce(`Rendering ${input.length} clips.`);
        for (let i = 0; i < input.length; i += 1) {
          if (canceledRef.current && clipTextsFinishedRendering.length) {
            trackBulkClipCreationMixPanelEvent(
              clipTextsFinishedRendering,
              charactersUsed,
              renderMode
            );
            return resetState();
          }
          const plainText = input[i];
          // Render mode is sentence or paragraph so formattedText will never be populated
          const formattedTextChunk = undefined;
          // eslint-disable-next-line no-await-in-loop
          await renderClip(plainText, formattedTextChunk, renderMode);
          clipTextsFinishedRendering.push(plainText);
        }

        announce('New clips rendered.');
      }

      trackBulkClipCreationMixPanelEvent(
        clipTextsFinishedRendering,
        charactersUsed,
        renderMode
      );

      resetState();
    },
    [
      editorState,
      getFormattedText,
      verifyProductAccessLimits,
      trackBulkClipCreationMixPanelEvent,
      resetState,
      announce,
      renderClip,
    ]
  );

  const cancelFn = useCallback(async () => {
    canceledRef.current = true;
    setState({ ...state, loading: false, error: null });

    if (state.currentClipId) {
      removeClipFromCache({ id: state.currentClipId }, projectId);
      await cancelRendering({
        variables: {
          inputs: {
            id: state.currentClipId,
            projectId,
          },
        },
      });
    }

    eventTracking.track({
      type: EventName.ClipRenderingCanceled,
    });
  }, [cancelRendering, eventTracking, projectId, removeClipFromCache, state]);

  useEffect(() => {
    // In the event that the user's subscription data changes let's clear the clip creator error
    // message.
    setState((previousState: ClipCreatorState) => ({
      ...previousState,
      error: null,
    }));
  }, [subscriptionData]);

  return [
    submitFn,
    state,
    cancelFn,
    previewFn,
    previewClip,
    resetPreviewClip,
    resetPreviewClipAndState,
  ];
}

/**
 * determinePendingReplacements takes the raw editor input as a string and finds phonetic
 * replacements denoted with :: and returns an array of sanitized, deduped replacement words
 */
export function determinePendingReplacements(
  editorValue: string
): Array<string> {
  let pendingReplacements: Array<string> = [];
  const phoneticSpellingRegex = XRegExp(`::[a-zA-Z\\-]+::`);
  const matches: Array<string> = [];
  let pos = 0;
  let match: ExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((match = XRegExp.exec(editorValue, phoneticSpellingRegex, pos))) {
    matches.push(match[0]);
    pos = match.index + 1;
  }
  pendingReplacements = uniq(
    matches.map(replacement => replacement.slice(2, -2))
  );
  return pendingReplacements;
}

export default useClipCreator;
